/**
 * ミッション環境ビヘイビアで共有するヘルパ関数。
 *
 * rjf01〜rjf12 に重複していた「名前引き・ID 引き・一時 ID 採番・AABB 重なり
 * 判定・アバター接触判定・テンプレート複製」を 1 箇所へ集約する。各ミッション
 * 固有のロジック（ボス AI・タイマー・クローン属性など）はここには置かず、
 * 副作用の少ない汎用ヘルパだけを共有する（DRY と単一責任の両立）。
 */
import {
  AVATAR_ID,
  EditablePreviewWorld,
  ObjectRuntimeState,
  Vec3,
} from '../types'
import { BehaviorContext, BehaviorHandler, HudStyle } from './types'

/**
 * 名前からオブジェクトの現在状態を引く。重複名は最初の 1 件を返す。
 * 名前が見つからない（ハードコードシーン等）場合は undefined。
 */
export const findStateByName = (
  world: EditablePreviewWorld,
  name: string,
): ObjectRuntimeState | undefined => {
  for (const state of world.getState().values()) {
    if (state.name === name) {
      return state
    }
  }
  return undefined
}

/** 名前からオブジェクト ID を引く（見つからなければ undefined）。 */
export const findIdByName = (
  world: EditablePreviewWorld,
  name: string,
): string | undefined => findStateByName(world, name)?.id

/** 名前からオブジェクトの現在位置を引く（見つからなければ undefined）。 */
export const findPositionByName = (
  world: EditablePreviewWorld,
  name: string,
): Vec3 | undefined => findStateByName(world, name)?.position

/** control_clone のクローン名サフィックス（interpreter の命名規約）。 */
const CLONE_SUFFIX_PATTERN = /#clone\d+$/

/**
 * クローン名（元名#cloneN）から元名を取り出す。クローン名でなければ undefined。
 * 入れ子クローン（元名#clone1#clone2）は直接の複製元（元名#clone1）を返すため、
 * テンプレート名とは一致しない（複製元のクローンから状態を引き継ぎ済み）。
 */
export const cloneBaseName = (name: string): string | undefined => {
  const match = CLONE_SUFFIX_PATTERN.exec(name)
  return match ? name.slice(0, match.index) : undefined
}

/**
 * オブジェクト名がテンプレート名そのもの、またはそのクローン名（元名#cloneN）
 * かを判定する。実機の Clone() は同名複製のため、名前判定がクローンにも効く
 * 挙動の再現に使う。入れ子クローン（元名#clone1#clone2）は一致しない
 * （cloneBaseName が直接の複製元名を返すため）。
 */
export const matchesTemplateOrClone = (actual: string, base: string): boolean =>
  actual === base || cloneBaseName(actual) === base

/**
 * 既存 ID と衝突しない一時オブジェクト ID を返す（addObject は重複で throw）。
 * 連番はモジュール寿命で単調増加させ、プレイをまたいで同じ ID を再利用しない
 * （停止直後の旧プレイの破棄処理が、新プレイで同 ID に生成し直した
 * オブジェクトを誤って消す競合を防ぐ）。
 */
let tempIdSerial = 0
export const nextTempId = (
  world: EditablePreviewWorld,
  prefix: string,
): string => {
  let id: string
  do {
    tempIdSerial += 1
    id = `${prefix}_${tempIdSerial}`
  } while (world.exists(id))
  return id
}

/**
 * 2 つの箱（中心・half extents）が全軸で重なっているか（AABB 近似）。
 * size 版（overlapsAabb）が委譲する低レベル判定。
 */
export const overlapsAabbByHalf = (
  positionA: Vec3,
  halfA: Vec3,
  positionB: Vec3,
  halfB: Vec3,
): boolean =>
  Math.abs(positionA.x - positionB.x) <= halfA.x + halfB.x &&
  Math.abs(positionA.y - positionB.y) <= halfA.y + halfB.y &&
  Math.abs(positionA.z - positionB.z) <= halfA.z + halfB.z

/**
 * 2 つの箱（中心・寸法 size）が全軸で重なっているか（AABB 近似）。
 * size を half extents（size / 2）へ変換して overlapsAabbByHalf へ委譲する。
 */
export const overlapsAabb = (
  positionA: Vec3,
  sizeA: Vec3,
  positionB: Vec3,
  sizeB: Vec3,
): boolean =>
  overlapsAabbByHalf(
    positionA,
    { x: sizeA.x / 2, y: sizeA.y / 2, z: sizeA.z / 2 },
    positionB,
    { x: sizeB.x / 2, y: sizeB.y / 2, z: sizeB.z / 2 },
  )

/**
 * 指定の位置・寸法の箱がアバターと AABB で重なっているか。
 * アバターがシーンに居ない（未生成）場合は false。
 */
export const touchesAvatar = (
  world: EditablePreviewWorld,
  position: Vec3,
  size: Vec3,
): boolean => {
  const avatar = world.getState().get(AVATAR_ID)
  if (!avatar) {
    return false
  }
  return overlapsAabb(avatar.position, avatar.size, position, size)
}

/**
 * テンプレートと同じ見た目のクローンを生成し、その ID を返す。
 * 位置・寸法・色・姿勢などをテンプレートの現在状態から複製する
 * （Roblox の Clone() が CFrame・見た目を引き継ぐのと同じ）。生成物は
 * 実行時生成（Anchored 固定）として作る。
 */
export const spawnClone = (
  world: EditablePreviewWorld,
  template: ObjectRuntimeState,
  prefix: string,
): string => {
  const id = nextTempId(world, prefix)
  world.addObject({
    id,
    name: template.name,
    position: { ...template.position },
    size: { ...template.size },
    color: template.color,
    material: template.material,
    physicalProperties: template.physicalProperties,
    rotationY: template.rotationY,
    orientation: template.orientation,
    transparency: template.transparency,
    canCollide: template.canCollide,
    canTouch: template.canTouch,
    isStatic: true,
    shape: template.shape,
    fire: template.fire,
    sparkle: template.sparkle,
    collisionScale: template.collisionScale,
  })
  return id
}

/**
 * テンプレートのスナップショットからクローンを 1 つ生成し（spawnClone へ委譲）、
 * 位置の指定があれば配置してから、テンプレート名に学習者が付けたプログラムを
 * クローンで起動する（Roblox の Clone() → Position 代入 → Parent 設定で配下の
 * Script が走り出す流れの再現）。各ミッションで重複していた
 * spawnClone + setPosition + startObjectPrograms の連鎖を 1 箇所へ集約する。
 * 生成したクローンの ID を返す。
 */
export const spawnProgrammedClone = (
  ctx: BehaviorContext,
  snapshot: ObjectRuntimeState,
  prefix: string,
  position?: Vec3,
): string => {
  const id = spawnClone(ctx.world, snapshot, prefix)
  if (position) {
    ctx.world.setPosition(id, { ...position })
  }
  ctx.startObjectPrograms?.(id, snapshot.name)
  return id
}

/**
 * 固定文言の HUD 表示ハンドラを作る。各ミッションに重複していた
 * 「(ctx) => ctx.setHudText?.(TEXT, STYLE)」形の一行ハンドラ
 * （初期表示・開始指示など）を 1 箇所へ集約する。slot を渡すと、その
 * スロットへ表示する（指示とカウントの同時表示など。省略時は 'default'）。
 */
export const showHudText = (
  text: string,
  style: HudStyle,
  slot?: string,
): BehaviorHandler => {
  return (ctx) => {
    ctx.setHudText?.(text, style, slot)
  }
}
